import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { Row as AriaRow } from 'react-aria-components';
import { CheckboxCell } from './CheckboxCell';
import { useBetaDataTableContext } from './Context';
import styles from './DataTable.module.scss';
import { buildStickyProps, useStickyColumnsContext } from './useStickyColumns';

export type BetaDataTableRowProps = {
  /** Whether to render a checkbox for the row */
  allowsSelection?: boolean;
  /** Row content. Use `Cell` from `react-aria-components`, one per column */
  children: React.ReactNode;
  /** Additional CSS class name */
  className?: string;
  /** Render the column for a checkbox but hide it */
  hiddenSelection?: boolean;
  /** Unique identifier for the row */
  id: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
};

// Minimal type for the cloneElement cast inside the sticky code path.
// We only need to read/merge `style`; React Aria's Cell type is internal,
// so this intentionally avoids coupling to a specific Cell prop interface.
type CellLikeProps = {
  style?: React.CSSProperties;
};

export const BetaDataTableRow = ({
  allowsSelection,
  children,
  className,
  hiddenSelection,
  id,
  style,
}: BetaDataTableRowProps) => {
  const { isChecked, registerSelectableRow, unregisterSelectableRow } =
    useBetaDataTableContext();
  const {
    getStickyInfo,
    hasStickyColumns,
    canScrollLeft,
    canScrollRight,
    checkboxOffset,
  } = useStickyColumnsContext();

  const showCheckbox = allowsSelection === true || hiddenSelection === true;
  const isSelectable = allowsSelection === true && !hiddenSelection;

  useEffect(() => {
    if (isSelectable) {
      registerSelectableRow(id);
      return () => unregisterSelectableRow(id);
    }
  }, [id, isSelectable, registerSelectableRow, unregisterSelectableRow]);

  const renderedChildren = useMemo(() => {
    if (!hasStickyColumns) return children;
    return React.Children.map(children, (child, index) => {
      const absoluteIndex = index + checkboxOffset;
      const info = getStickyInfo(absoluteIndex);
      if (!info || !React.isValidElement(child)) return child;

      const cellChild = child as React.ReactElement<CellLikeProps>;
      const { style: stickyStyle, ...stickyDataAttrs } = buildStickyProps({
        info,
        canScrollLeft,
        canScrollRight,
      });
      return React.cloneElement(cellChild, {
        style: {
          ...cellChild.props.style,
          ...stickyStyle,
        },
        ...stickyDataAttrs,
      });
    });
  }, [
    children,
    hasStickyColumns,
    getStickyInfo,
    checkboxOffset,
    canScrollLeft,
    canScrollRight,
  ]);

  return (
    <AriaRow
      className={clsx(styles.tr, isChecked(id) && styles.trChecked, className)}
      style={style}
    >
      {showCheckbox && <CheckboxCell id={id} hidden={hiddenSelection} />}
      {renderedChildren}
    </AriaRow>
  );
};
